import {useEffect, useRef, useState} from 'react'

// Sets up a task to run automatically every [x] milliseconds.
// Typically used for polling an API endpoint.
export function usePolling(pollingFunc: () => void | Promise<void>, millisecs: number) {
  const timerIdRef = useRef<null | number>(null)
  const [isPageVisible, setIsPageVisible] = useState(!document.hidden)

  // Keep track of whether the user is on this tab or not. If not, we
  // will stop polling.
  useEffect(() => {
    const updateVisibility = () => {
      setIsPageVisible(!document.hidden)
    }

    document.addEventListener('visibilitychange', updateVisibility)

    return () => {
      document.removeEventListener('visibilitychange', updateVisibility)
    }
  }, [])

  // Set up the actual polling mechanism.
  useEffect(() => {
    // Start polling: Run the polling function immediately. Then set up
    // a repeating timer to run it again.
    const startPolling = () => {
      pollingFunc()
      timerIdRef.current = window.setInterval(pollingFunc, millisecs)
    }

    // Stop polling: Clear the repeating timer if it exists.
    const stopPolling = () => {
      if (timerIdRef.current !== null) {
        clearInterval(timerIdRef.current)
      }
    }

    // If the user has moved away from this tab, stop polling.
    // If they've come back, start again.
    if (isPageVisible) {
      startPolling()
    } else {
      stopPolling()
    }

    // When the component is unmounted, stop polling.
    return () => {
      stopPolling()
    }
  }, [isPageVisible])
}
